import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { uploadImagesToCos } from '@src/services/api';
import styles from './index.css';

const MAX_FILES = 5;
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const SINGLE_UPLOAD_LIMIT_MESSAGE = '最多上传5张图片';
const SINGLE_FILE_SIZE_LIMIT_MESSAGE = '单张图片不能超过5M';
const ACCEPT = {
    'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp', '.bmp'],
};

const normalizeImageItem = (item) => {
    if (!item) {
        return null;
    }
    if (typeof item === 'string') {
        return {
            id: item,
            previewUrl: item,
            uploadedUrl: item,
        };
    }
    if (item.previewUrl && item.uploadedUrl) {
        return item;
    }
    if (item.url) {
        return {
            id: item.id || item.url,
            previewUrl: item.previewUrl || item.url,
            uploadedUrl: item.uploadedUrl || item.url,
        };
    }
    return null;
};

/**
 * 图片上传组件 - 支持拖拽和点击上传，最多5张
 */
const ImageUpload = ({ files, onChange, error }) => {
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const normalizedFiles = (files || []).map(normalizeImageItem).filter(Boolean);

    const onDrop = useCallback(
        async (acceptedFiles) => {
            if (!acceptedFiles?.length) {
                return;
            }
            if (acceptedFiles.length > MAX_FILES) {
                setUploadError(SINGLE_UPLOAD_LIMIT_MESSAGE);
                return;
            }
            const hasOversizedFile = acceptedFiles.some(file => file.size > MAX_FILE_SIZE);
            if (hasOversizedFile) {
                setUploadError(SINGLE_FILE_SIZE_LIMIT_MESSAGE);
                return;
            }

            setUploading(true);
            setUploadError('');
            try {
                const uploadedUrls = await uploadImagesToCos(acceptedFiles);
                const uploadedItems = acceptedFiles.map((file, index) => {
                    const uploadedUrl = uploadedUrls[index];
                    return {
                        id: `${Date.now()}-${index}-${file.name}`,
                        previewUrl: URL.createObjectURL(file),
                        uploadedUrl,
                    };
                });
                const nextFiles = [...normalizedFiles, ...uploadedItems].slice(0, MAX_FILES);
                onChange(nextFiles);
            } catch (err) {
                setUploadError(err.message || '图片上传失败，请稍后重试');
            } finally {
                setUploading(false);
            }
        },
        [normalizedFiles, onChange]
    );

    const onDropRejected = useCallback((fileRejections) => {
        const hasTooManyFiles = fileRejections?.some((rejection) => rejection?.errors?.some((errorItem) => errorItem.code === 'too-many-files'));
        if (hasTooManyFiles) {
            setUploadError(SINGLE_UPLOAD_LIMIT_MESSAGE);
            return;
        }
        const hasOversizedFile = fileRejections?.some((rejection) => rejection?.errors?.some((errorItem) => errorItem.code === 'file-too-large'));
        if (hasOversizedFile) {
            setUploadError(SINGLE_FILE_SIZE_LIMIT_MESSAGE);
            return;
        }
        setUploadError('图片格式不支持');
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        onDropRejected,
        accept: ACCEPT,
        maxFiles: MAX_FILES - normalizedFiles.length,
        maxSize: MAX_FILE_SIZE,
        disabled: normalizedFiles.length >= MAX_FILES || uploading,
    });

    const removeImage = (index) => {
        const newFiles = [...normalizedFiles];
        const removed = newFiles[index];
        if (removed?.previewUrl?.startsWith('blob:')) {
            URL.revokeObjectURL(removed.previewUrl);
        }
        newFiles.splice(index, 1);
        onChange(newFiles);
    };

    const previewCount = normalizedFiles.length;
    const canAddMore = previewCount < MAX_FILES;

    return (
        <div className={styles.field}>
            <div className={styles.labelRow}>
                <span className={styles.label}>图片</span>
                <span className={styles.subtitle}>(最多上传{MAX_FILES}张图片，单张图片不能超过5M)</span>
            </div>
            <div className={styles.uploadArea}>
                {normalizedFiles.map((file, index) => (
                    <div key={`${file.id || file.uploadedUrl}-${index}`} className={styles.previewItem}>
                        <img src={file.previewUrl} alt={`预览${index + 1}`} className={styles.previewImg} />
                        <button type="button" className={styles.removeBtn} onClick={() => removeImage(index)} aria-label="删除图片" />
                    </div>
                ))}
                {canAddMore && (
                    <div {...getRootProps()} className={`${styles.dropzone} ${isDragActive ? styles.dropzoneActive : ''}`}>
                        <input {...getInputProps()} />
                        <span className={styles.uploadIcon}>+</span>
                        <span className={styles.uploadText}>{uploading ? '上传中...' : '点击上传'}</span>
                    </div>
                )}
            </div>
            <div className={styles.infotitle}>图片仅支持jpg、jpeg、png、gif、webp和bmp格式</div>
            {(uploadError || error) && <span className={styles.errorText}>{uploadError || error}</span>}
        </div>
    );
};

ImageUpload.propTypes = {
    files: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            previewUrl: PropTypes.string,
            uploadedUrl: PropTypes.string,
            url: PropTypes.string,
        })
    ),
    onChange: PropTypes.func.isRequired,
    error: PropTypes.string,
};

export default ImageUpload;
